// 042: hand-rolled 1–5 star rating (no new deps). Interactive when onChange is supplied (whole stars), otherwise a
// read-only display. Yellow #FFD800 fill per docs/DESIGN-spotify-app.md.
// 363 (#1090, FR-011): the display shows halves, rounded to the nearest half, so the stars agree with the decimal
// beside them (4.6 shows 4½).

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  size?: number;
}

type Fill = 'full' | 'half' | 'empty';

const FILLED = '#FFD800';
const EMPTY = '#4b5563'; // gray-600
const PATH = 'M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 15l-5.2 2.6 1-5.8L1.5 7.7l5.9-.9L10 1.5z';

function StarSvg({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color} aria-hidden="true">
      <path d={PATH} />
    </svg>
  );
}

/** A half star is the empty star with the filled one over its left half — no SVG ids to collide. */
function Star({ fill, size }: { fill: Fill; size: number }) {
  if (fill !== 'half') {
    return (
      <span data-fill={fill} className="inline-flex">
        <StarSvg color={fill === 'full' ? FILLED : EMPTY} size={size} />
      </span>
    );
  }
  return (
    <span data-fill="half" className="relative inline-flex">
      <StarSvg color={EMPTY} size={size} />
      <span
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: `${size / 2}px` }}
      >
        <StarSvg color={FILLED} size={size} />
      </span>
    </span>
  );
}

/** The fill of star `n` (1–5) for a displayed average, to the nearest half. */
function fillOf(n: number, value: number): Fill {
  const halves = Math.round(value * 2) / 2;
  if (n <= halves) return 'full';
  if (n - 0.5 === halves) return 'half';
  return 'empty';
}

export function StarRating({ value, onChange, size = 24 }: StarRatingProps) {
  const stars = [1, 2, 3, 4, 5];

  if (onChange) {
    return (
      <div className="flex gap-1" data-testid="star-rating">
        {stars.map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
            aria-pressed={n <= value}
            onClick={() => onChange(n)}
            className="p-0.5"
          >
            <Star fill={n <= value ? 'full' : 'empty'} size={size} />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-1" data-testid="star-rating" aria-label={`Rating: ${value} out of 5`}>
      {stars.map((n) => (
        <Star key={n} fill={fillOf(n, value)} size={size} />
      ))}
    </div>
  );
}
